// Fogged props: trees, peaks and buildings turn to sepia ink where the land under them is fogged, so
// the paper does not carry full-colour models (rendering.md 7.1). In the strategic view they dissolve
// away, since that view draws its own icons.
//
//   prop albedo ──► luma ──► sepia ink (dark where the prop is dark) ──► mixed by the fog cover
//   strategic amount ──► screen dither ──► discard the pixels below it
//
// The feature layer owns its materials and builds them on first use, so this module wraps their
// colour and mask nodes from outside: the originals are kept, and detach() puts them back. Cover
// comes from the same field, jitter and burn front as the land (parchment.ts), read at the prop's
// own XZ.

import * as THREE from 'three/webgpu';
import { float, interleavedGradientNoise, mix, positionWorld, screenCoordinate } from 'three/tsl';
import type Node from 'three/src/nodes/core/Node.js';
import { rgb } from '../tsl/color.ts';
import { hexFrag } from '../tsl/hex.ts';
import type { FogState } from './fogState.ts';
import { fieldAt, fogLook } from './parchment.ts';
import { luma } from './patterns.ts';

type F = Node<'float'>;
type V3 = Node<'vec3'>;

/** How much of a prop turns to ink at full fog. */
const INK_SHARE = 0.9;
/** Under a lens the fog steps back this share, as on the land (fog.ts). */
const LENS_FOG_CUT = 0.8;
/** Sepia albedo: ink brown scaled by the prop's own luma, so shading and silhouettes survive. */
const SEPIA_INK = '#6b4a2a';
const SEPIA_GAIN = 3.2;
const SEPIA_FLOOR = 0.35;
const SEPIA_CEIL = 1.5;

type ColorNode = THREE.MeshStandardNodeMaterial['colorNode'];
type MaskNode = THREE.MeshStandardNodeMaterial['maskNode'];

interface Wrapped {
  material: THREE.MeshStandardNodeMaterial;
  original: ColorNode;
  originalMask: MaskNode;
}

export interface FeatureFog {
  /** Wrap the colour node of every prop material under `group` that is not wrapped yet. */
  attach(group: THREE.Object3D): void;
  /** Put the original colour nodes back. */
  detach(): void;
}

export function createFeatureFog(state: FogState, lensAmount: F, strategicAmount: F): FeatureFog {
  const wrapped = new Map<THREE.Material, Wrapped>();

  /** Albedo → sepia ink albedo, by the fog cover at the prop's XZ. */
  function fade(albedo: V3): V3 {
    const xz = positionWorld.xz;
    const field = fieldAt(state.data, hexFrag(xz, 'propHex'));
    const cover = fogLook(field, xz, state.reveal).cover.mul(INK_SHARE).mul(float(1).sub(lensAmount.mul(LENS_FOG_CUT)));
    const tone = luma(albedo).mul(SEPIA_GAIN).clamp(SEPIA_FLOOR, SEPIA_CEIL);
    return mix(albedo, rgb(SEPIA_INK).mul(tone), cover);
  }

  /** True where the prop stays: a screen dither against the strategic amount, so it thins out evenly. */
  function dissolve(): Node<'bool'> {
    return interleavedGradientNoise(screenCoordinate.xy).greaterThanEqual(strategicAmount);
  }

  function wrap(material: THREE.MeshStandardNodeMaterial): void {
    if (wrapped.has(material) || !material.colorNode) {
      return;
    }
    wrapped.set(material, { material, original: material.colorNode, originalMask: material.maskNode });
    material.colorNode = fade(material.colorNode as unknown as V3) as unknown as ColorNode;
    material.maskNode = dissolve() as unknown as MaskNode;
    material.needsUpdate = true;
  }

  return {
    attach(group) {
      group.traverse((object) => {
        if (!(object instanceof THREE.InstancedMesh)) {
          return;
        }
        const materials = Array.isArray(object.material) ? object.material : [object.material];
        for (const material of materials) {
          if (material instanceof THREE.MeshStandardNodeMaterial) {
            wrap(material);
          }
        }
      });
    },
    detach() {
      for (const { material, original, originalMask } of wrapped.values()) {
        material.colorNode = original;
        material.maskNode = originalMask;
        material.needsUpdate = true;
      }
      wrapped.clear();
    },
  };
}
